import { useEffect, useMemo, useRef, useState } from 'react';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { useLocation, useNavigate, Location } from 'react-router-dom';
import { Wizard } from '../wizard';
import { WizardStepTracker } from '../interfaces/wizard-step-tracker';
import { WizardController } from '../interfaces/wizard-controller';
import {
  WizardCurrentStep,
  WizardOptions,
  WizardStateWrapper,
  WizardStepTree,
  WizardStepTreeSnapshot,
  WizardStorageData
} from '../interfaces/wizard-step';
import { WizardQueryParamHandler } from '../interfaces/wizard-query-param-handler';
import { WizardStateMachineInterface } from '../interfaces/wizard-state-machine';
import { useWizardStorage } from './useWizardStorage';

interface WizardState {
  wizard: Wizard | null;
  stepTracker: WizardStepTracker | null;
  wizardController: WizardController | null;
  currentStep: WizardCurrentStep | null;
  wizardState: WizardStateWrapper | null;
  stepTreeSnapshot: WizardStepTreeSnapshot | null;
  stepGroups: WizardStepTree | null;
}

const wizardDefaultState: WizardState = {
  wizard: null,
  stepTracker: null,
  wizardController: null,
  currentStep: null,
  wizardState: null,
  stepTreeSnapshot: null,
  stepGroups: null
};

export function useWizard({
  onComplete,
  initialCache,
  cancelUrl,
  isCancelUrlPathRelative
}: {
  initialCache?: Map<string, WizardStorageData>;
  onComplete: VoidFunction;
  cancelUrl: string;
  isCancelUrlPathRelative?: boolean;
}) {
  const [currentStepFooterProps, setCurrentStepFooterProps] = useState<object>({});

  const navigate = useNavigate();
  const location = useLocation();
  const storage = useWizardStorage(initialCache);

  const locationSub = useRef(new BehaviorSubject<Location>(location));

  const [wizard, setWizard] = useState<WizardState>(wizardDefaultState);
  const [isWizardCompleted, setIsWizardCompleted] = useState(false);

  useEffect(() => {
    locationSub.current.next(location);
  }, [location]);

  const getCurrentLocation = () => {
    return locationSub.current.getValue();
  };

  const getCurrentLocationSearchParams = () => {
    return new URLSearchParams(getCurrentLocation().search);
  };

  const queryParamsHandler: WizardQueryParamHandler = useMemo(() => {
    return {
      getPath: (queryParamKey: string) => {
        return getCurrentLocationSearchParams().get(queryParamKey);
      },

      updatePath: (queryParamKey: string, path: string, replaceUrl?: boolean) => {
        const urlSearchParams = getCurrentLocationSearchParams();
        urlSearchParams.set(queryParamKey, path);

        const navigationPath = {
          search: urlSearchParams.toString()
        };

        if (replaceUrl) {
          navigate(navigationPath, { replace: true });
        } else {
          navigate(navigationPath);
        }

        location.search = `?${urlSearchParams.toString()}`;
        locationSub.current.next(location);
      },

      changes: (queryParamKey: string, callback: VoidFunction) => {
        const subject = new Subject<string | null>();
        const initialLocation = getCurrentLocation();

        const setQueryParams = (updatedLocation = { ...initialLocation }) => {
          const urlSearchParams = new URLSearchParams(updatedLocation.search);
          subject.next(urlSearchParams.get(queryParamKey));
        };

        const historyListener = locationSub.current.subscribe(l => {
          setQueryParams(l);
        });

        const subscription = subject.pipe(distinctUntilChanged()).subscribe(() => {
          callback();
        });

        setQueryParams();

        return () => {
          historyListener.unsubscribe();
          subscription.unsubscribe();
        };
      }
    };
  }, []);

  const createWizard = ({
    stepsHeader,
    options,
    stateMachine
  }: {
    stepsHeader: WizardStepTree;
    options: WizardOptions;
    stateMachine: WizardStateMachineInterface;
  }) => {
    const _stepTracker = new WizardStepTracker(stepsHeader, storage, options.trackingOptions);
    const _wizardController = new WizardController(_stepTracker);
    const _wizard = new Wizard(_wizardController, stateMachine, _stepTracker, queryParamsHandler);

    _wizardController.setWizardInstance(_wizard);

    setWizard(prevState => ({
      ...prevState,
      wizard: _wizard,
      stepTracker: _stepTracker,
      wizardController: _wizardController
    }));
  };

  useEffect(() => {
    if (!wizard || !wizard.wizardController) {
      return;
    }

    const stateSub = wizard.wizardController.wizardState$.subscribe(wizardState => {
      setWizard(prevState => ({
        ...prevState,
        wizardState
      }));
    });

    const stepSub = wizard.wizardController.currentStep$.subscribe(step => {
      setWizard(prevState => ({
        ...prevState,
        currentStep: step
      }));
    });

    const completeSub = wizard.wizardController.complete.subscribe(() => {
      setIsWizardCompleted(true);
      onComplete();
    });

    const snapshotSub = wizard.wizardController
      .getStepTracker()
      .stepTreeSnapshot$.subscribe(snapshot => {
        setWizard(prevState => ({
          ...prevState,
          stepTreeSnapshot: snapshot
        }));
      });

    const stepGroupsSub = wizard.wizardController
      .getStepTracker()
      .treeStepGroups$.subscribe(stepGroups => {
        setWizard(prevState => ({
          ...prevState,
          stepGroups
        }));
      });

    const footerPropsSub = wizard.wizardController
      .getStepTracker()
      .footerProps$.subscribe(props => {
        setCurrentStepFooterProps(props);
      });

    // eslint-disable-next-line consistent-return
    return () => {
      stateSub.unsubscribe();
      stepSub.unsubscribe();
      completeSub.unsubscribe();
      snapshotSub.unsubscribe();
      stepGroupsSub.unsubscribe();
      footerPropsSub.unsubscribe();
      wizard.wizard?.destroy();
    };
  }, [wizard?.wizardController]);

  const selectStepGroup = (stepGroups: WizardStepTree, index: number) => {
    if (!wizard || !wizard.wizardController) {
      return;
    }

    const steps = wizard.wizardController
      .getStepTracker()
      .getSteps()
      .filter(step => {
        return step.path.startsWith(stepGroups.children[index].for);
      });

    if (steps.length === 0) {
      return;
    }

    const firstIncompleteStep = steps.find(step => !step.complete);

    if (firstIncompleteStep) {
      wizard.wizardController.selectStep(firstIncompleteStep.path);
      return;
    }

    const allStepsCompleted = steps.filter(step => step.complete).length === steps.length;

    if (allStepsCompleted) {
      wizard.wizardController.selectStep(steps[steps.length - 1].path);
    } else {
      wizard.wizardController.selectStep(steps[0].path);
    }
  };

  const onBackClick = () => {
    wizard.wizardController?.goBack();
  };

  const onCancelClick = () => {
    navigate(cancelUrl, { replace: true, relative: isCancelUrlPathRelative ? 'path' : 'route' });
  };

  return {
    wizardState: wizard?.wizardState,
    currentStep: wizard?.currentStep,
    stepTracker: wizard?.stepTracker,
    controller: wizard?.wizardController,
    stepTreeSnapshot: wizard?.stepTreeSnapshot,
    stepGroups: wizard?.stepGroups,
    wizardCreated: !!wizard,
    wizardCompleted: isWizardCompleted || !!wizard?.wizardController?.isCompleted,
    currentStepFooterProps,
    selectStepGroup,
    createWizard,
    onBackClick,
    onCancelClick
  };
}
